/** ONE Finance Vault — Credit Card Payment Command Centre.
 *  Single source of truth (GET /card-control/payment-centre + /overview):
 *  KPI strip (current-cycle vs historical kept separate), PAY TODAY checklist,
 *  ranked Payment Priority, and a shared Record-Payment drawer with split
 *  allocation + duplicate protection. */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, CheckCircle2, Clock, CreditCard, History, Loader2,
  Wallet, CalendarClock, Upload,
} from "lucide-react";

const PAID_THROUGH = ["CRED", "Bank Transfer", "NEFT", "RTGS", "IMPS", "UPI", "Net Banking",
  "Auto Debit", "Debit Card", "Cheque", "Cash", "Wallet", "Other"];
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money0 = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const LABELS = {
  OVERDUE: { t: "OVERDUE", c: "bg-rose-100 text-rose-700 border-rose-200" },
  TARGET_MISSED: { t: "TARGET DATE MISSED", c: "bg-orange-100 text-orange-700 border-orange-200" },
  PAY_TODAY: { t: "PAY TODAY", c: "bg-orange-100 text-orange-700 border-orange-200" },
  DUE_IN_3: { t: "DUE IN 3 DAYS", c: "bg-amber-100 text-amber-700 border-amber-200" },
  DUE_THIS_WEEK: { t: "DUE THIS WEEK", c: "bg-amber-50 text-amber-700 border-amber-200" },
  PARTIALLY_PAID: { t: "PARTIALLY PAID", c: "bg-blue-50 text-blue-700 border-blue-200" },
  DUE_LATER: { t: "DUE LATER", c: "bg-slate-100 text-slate-600 border-slate-200" },
  MANUAL: { t: "MANUALLY ADDED", c: "bg-violet-50 text-violet-700 border-violet-200" },
};
const badge = (label) => LABELS[label] || LABELS.DUE_LATER;

export default function PaymentCentre({ overview, onChanged, onOpenCard }) {
  const [pc, setPc] = useState(null);
  const [pay, setPay] = useState(null);          // {row} for record-payment drawer
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/card-control/payment-centre"); setPc(data); }
    catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const k = overview?.payment_kpis || pc?.kpis || {};
  const payToday = pc?.pay_today || [];
  const ptk = pc?.pay_today_kpi || { cards: 0, remaining: 0, count: 0 };
  const priorityCount = (pc?.priority || []).length;

  const shown = useMemo(() => {
    const priority = pc?.priority || [];
    const f = filter;
    return priority.filter((r) => {
      if (f === "ALL") return true;
      if (f === "OVERDUE") return r.priority_label === "OVERDUE";
      if (f === "TARGET_MISSED") return r.priority_label === "TARGET_MISSED";
      if (f === "WEEK") return ["PAY_TODAY", "DUE_IN_3", "DUE_THIS_WEEK", "TARGET_MISSED", "OVERDUE"].includes(r.priority_label);
      if (f === "PARTIAL") return r.paid_amount > 0 && r.outstanding > 0;
      if (f === "HISTORICAL") return r.is_historical;
      if (f === "CURRENT") return !r.is_historical;
      return true;
    });
  }, [pc, filter]);

  const refresh = () => { load(); onChanged?.(); };

  const kpis = [
    { l: "Current cycle due", v: money0(k.current_cycle_due), sub: `${k.current_cycle_count || 0} statements`, f: "CURRENT", tone: "text-slate-900", help: "Amount due on the latest statements." },
    { l: "Historical open", v: money0(k.historical_open_balance), sub: `${k.historical_open_count || 0} statements`, f: "HISTORICAL", tone: k.historical_open_balance ? "text-blue-700" : "text-slate-900", help: "Unsettled balance on previous statements. May already be included in the latest statement." },
    { l: "Overdue", v: money0(k.overdue_amount), sub: `${k.overdue_count || 0} statements`, f: "OVERDUE", tone: k.overdue_amount ? "text-rose-700" : "text-slate-900", help: "Past-due — bank due date has passed." },
    { l: "Target this week", v: money0(k.target_week_amount), sub: `${k.target_week_cards || 0} cards`, f: "WEEK", tone: k.target_week_amount ? "text-amber-700" : "text-slate-900", help: "Target Pay By within the next 7 days." },
    { l: "Payments due", v: k.payments_due_count || 0, sub: "open obligations", f: "ALL", tone: "text-slate-900", help: "All open statement obligations." },
  ];

  return (
    <div className="space-y-4" data-testid="payment-centre">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {kpis.map((t) => (
          <button key={t.l} onClick={() => setFilter(t.f)} title={t.help} data-testid={`pc-kpi-${t.f.toLowerCase()}`}
            className={`text-left rounded-lg border px-3 py-2 transition-colors hover:bg-slate-50 ${filter === t.f ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200 bg-white"}`}>
            <div className="text-[9px] uppercase tracking-wider text-slate-400">{t.l}</div>
            <div className={`text-[16px] font-semibold ${t.tone}`}>{t.v}</div>
            <div className="text-[10px] text-slate-400">{t.sub}</div>
          </button>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 -mt-2">
        Current cycle and historical open are shown separately and never summed automatically — a previous balance may already be carried into the latest statement.
      </div>

      {/* PAY TODAY */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="pc-pay-today">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-orange-600" />
            <span className="text-[13px] font-semibold text-slate-900">Pay Today</span>
          </div>
          <div className="text-[11px] text-slate-500">
            {ptk.count ? <>{ptk.cards} card{ptk.cards === 1 ? "" : "s"} · <b className="text-slate-800">{money0(ptk.remaining)}</b> remaining</> : "—"}
          </div>
        </div>
        {payToday.length === 0 ? (
          <div className="px-4 py-6 text-center" data-testid="pc-caught-up">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto" strokeWidth={1.5} />
            <div className="text-[13px] font-medium text-slate-700 mt-1.5">Nothing to pay today</div>
            <div className="text-[11px] text-slate-400">All tracked card payments are within schedule.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {payToday.map((r) => (
              <PriorityRow key={r.statement_id} r={r} label={r.pay_today_label || r.priority_label}
                onPay={() => setPay(r)} onCard={() => onOpenCard?.(r.card_id)} compact />
            ))}
          </div>
        )}
      </div>

      {/* PAYMENT PRIORITY */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="pc-priority">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-[13px] font-semibold text-slate-900">Payment Priority</span>
            <span className="text-[11px] text-slate-400">{shown.length} of {priorityCount}</span>
          </div>
          <div className="flex gap-1 flex-wrap">
            {[["ALL", "All"], ["OVERDUE", "Overdue"], ["TARGET_MISSED", "Target missed"], ["WEEK", "This week"], ["PARTIAL", "Partial"], ["HISTORICAL", "Historical"]].map(([f, l]) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`pc-filter-${f.toLowerCase()}`}
                className={`text-[10px] px-2 py-0.5 rounded-full border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </div>
        {!pc ? (
          <div className="px-4 py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : shown.length === 0 ? (
          <div className="px-4 py-8 text-center" data-testid="pc-priority-empty">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto" strokeWidth={1.5} />
            <div className="text-[13px] font-medium text-slate-700 mt-1.5">All caught up</div>
            <div className="text-[11px] text-slate-400">No credit card payments currently require action.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {shown.map((r) => (
              <PriorityRow key={r.statement_id} r={r} label={r.priority_label}
                onPay={() => setPay(r)} onCard={() => onOpenCard?.(r.card_id)} />
            ))}
          </div>
        )}
      </div>

      {pay && (
        <RecordPaymentDrawer row={pay} openRows={(pc?.priority || []).filter((p) => p.card_id === pay.card_id)}
          onClose={() => setPay(null)} onSaved={() => { setPay(null); refresh(); }} />
      )}
    </div>
  );
}

function PriorityRow({ r, label, onPay, onCard, compact }) {
  const b = badge(label);
  const pct = r.total_due > 0 ? Math.min(100, Math.round((r.paid_amount / r.total_due) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50" data-testid={`pc-row-${r.statement_id}`}>
      <div className="w-10 h-6 rounded overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center">
        {r.artwork_thumb ? <img src={r.artwork_thumb} alt="" className="w-full h-full object-cover" /> : <CreditCard className="h-3.5 w-3.5 text-slate-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium text-slate-900 truncate">{r.card_name}</span>
          <span className="text-[10px] text-slate-400">•••• {r.last4}</span>
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${b.c}`}>{b.t}</span>
          {r.is_historical && <span className="text-[9px] px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">HISTORICAL</span>}
          {r.carry_forward_status === "INCLUDED" && <span className="text-[9px] text-emerald-600">✓ carried forward</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {r.period_label} · Remaining <b className="text-slate-800">{money0(r.outstanding)}</b>
          {r.paid_amount > 0 && <> of {money0(r.total_due)} ({pct}% paid)</>}
          {" · Target "}{r.target_pay_by || "—"} · Bank due {r.due_date || "—"}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button size="sm" className="h-7 bg-indigo-700 hover:bg-indigo-800 text-white text-[11px] px-2.5" onClick={onPay} data-testid={`pc-pay-${r.statement_id}`}>Record payment</Button>
        {!compact && onCard && <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={onCard} data-testid={`pc-view-${r.statement_id}`}>View card</Button>}
      </div>
    </div>
  );
}

export function RecordPaymentDrawer({ row, openRows, onClose, onSaved }) {
  const [f, setF] = useState({
    amount: row?.outstanding ? String(row.outstanding) : "",
    payment_date: todayStr(), paid_through: "Bank Transfer", paid_through_other: "",
    reference_id: "", notes: "",
  });
  const [proofUrl, setProofUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const uploadProof = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/card-control/payments/upload?card_id=${row.card_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setProofUrl(data.proof_url); toast.success("Proof attached"); }
    catch { toast.error("Upload failed"); }
  };

  const doSave = async (force = false) => {
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Enter an amount"); return; }
    if (f.paid_through === "Other" && !f.paid_through_other.trim()) { toast.error("Enter the payment method"); return; }
    setBusy(true);
    try {
      await api.post("/card-control/payments", {
        card_id: row.card_id, statement_id: row.statement_id, payment_date: f.payment_date,
        amount: Number(f.amount), paid_through: f.paid_through, paid_through_other: f.paid_through_other || null,
        reference_id: f.reference_id || null, notes: f.notes || null, proof_url: proofUrl || null, force,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.duplicate) { setDupWarn(d); }
      else { toast.error(typeof d === "string" ? d : "Failed to record payment"); }
    } finally { setBusy(false); }
  };

  const remaining = row.outstanding;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pc-record-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Record payment · {row.card_name} •••• {row.last4}</DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-slate-500 -mt-2">
          {row.period_label} statement · Remaining <b className="text-slate-800">{money(remaining)}</b> · Target {row.target_pay_by || "—"}
        </div>
        {dupWarn && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="pc-dup-warn">
            <b>Possible duplicate.</b> {dupWarn.message} (Ref {dupWarn.existing?.reference_id}, {money(dupWarn.existing?.amount)} on {dupWarn.existing?.payment_date}). Save anyway?
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[12px]">Amount paid</Label>
            <Input type="number" value={f.amount} onChange={(e) => u("amount", e.target.value)} data-testid="pc-amount" className="mt-1" />
            <button className="text-[10px] text-indigo-600 mt-1" onClick={() => u("amount", String(remaining))} data-testid="pc-pay-full">Pay full remaining ({money0(remaining)})</button>
          </div>
          <div><Label className="text-[12px]">Date</Label>
            <Input type="date" value={f.payment_date} onChange={(e) => u("payment_date", e.target.value)} data-testid="pc-date" className="mt-1" /></div>
          <div><Label className="text-[12px]">Paid through</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white mt-1" value={f.paid_through} onChange={(e) => u("paid_through", e.target.value)} data-testid="pc-method">
              {PAID_THROUGH.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
          {f.paid_through === "Other"
            ? <div><Label className="text-[12px]">Payment method</Label><Input value={f.paid_through_other} onChange={(e) => u("paid_through_other", e.target.value)} data-testid="pc-method-other" className="mt-1" /></div>
            : <div><Label className="text-[12px]">Reference ID</Label><Input value={f.reference_id} onChange={(e) => u("reference_id", e.target.value)} placeholder="UTR / Txn / CRED ref" data-testid="pc-ref" className="mt-1" /></div>}
          {f.paid_through === "Other" && <div><Label className="text-[12px]">Reference ID</Label><Input value={f.reference_id} onChange={(e) => u("reference_id", e.target.value)} data-testid="pc-ref2" className="mt-1" /></div>}
          <div className="col-span-2"><Label className="text-[12px]">Notes</Label><Input value={f.notes} onChange={(e) => u("notes", e.target.value)} className="mt-1" /></div>
          <div className="col-span-2"><Label className="text-[12px]">Payment proof (optional)</Label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={uploadProof} className="text-[12px] mt-1" data-testid="pc-proof" />
            {proofUrl && <span className="text-[10px] text-emerald-600 ml-2">Attached</span>}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {dupWarn
            ? <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => doSave(true)} disabled={busy} data-testid="pc-save-force">Save anyway</Button>
            : <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={() => doSave(false)} disabled={busy} data-testid="pc-save">{busy ? "Saving…" : "Save payment"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
